import React from "react";

const TEAL    = "#34656D";
const BORDER  = "1px solid #E2E8F0";

interface Props {
  /** number of fake data columns to render (default 5) */
  cols?: number;
  /** number of fake body rows (default 9) */
  rows?: number;
}

/** Reusable table skeleton for all Generation sheet tabs */
export const SheetTableSkeleton: React.FC<Props> = ({ cols = 5, rows = 9 }) => {
  const colArr  = Array.from({ length: cols });
  const rowArr  = Array.from({ length: rows });

  /* Shimmer block — each block pulses with a slight stagger */
  const Bar: React.FC<{
    w?: number | string;
    h?: number;
    delay?: number;
    dark?: boolean;
  }> = ({ w = "100%", h = 14, delay = 0, dark = false }) => (
    <div
      className="rounded animate-pulse"
      style={{
        width: w,
        height: h,
        backgroundColor: dark ? "#b8d8dc" : "#cde8eb",
        animationDelay: `${delay}ms`,
        flexShrink: 0,
      }}
    />
  );

  return (
    <div
      className="flex-1 min-h-0 rounded-xl overflow-hidden flex flex-col bg-white"
      style={{ border: BORDER, boxShadow: "0 2px 12px rgba(52,101,109,0.08)" }}
    >
      {/* ── Header row ─────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4"
        style={{
          height: 46,
          backgroundColor: "#EDF4F5",
          borderBottom: `2px solid #CAD5E2`,
          flexShrink: 0,
        }}
      >
        {/* sticky-col placeholder */}
        <Bar w={100} h={16} dark />
        {/* divider */}
        <div style={{ width: 2, height: 24, backgroundColor: TEAL, flexShrink: 0, borderRadius: 1 }} />
        {colArr.map((_, i) => (
          <Bar key={i} h={14} delay={i * 40} />
        ))}
      </div>

      {/* ── Body rows ──────────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden flex-1">
        {rowArr.map((_, ri) => (
          <div
            key={ri}
            className="flex items-center gap-3 px-4"
            style={{
              height: 37,
              backgroundColor: ri % 2 === 0 ? "#ffffff" : "#F7FAFB",
              borderBottom: BORDER,
              flexShrink: 0,
            }}
          >
            {/* time-slot column */}
            <Bar w={80} h={12} delay={ri * 30} dark />
            <div style={{ width: 2, height: 20, backgroundColor: "#CAD5E2", flexShrink: 0, borderRadius: 1 }} />
            {colArr.map((_, ci) => (
              <Bar
                key={ci}
                w={`${55 + ((ri + ci) % 3) * 10}%`}
                h={12}
                delay={ri * 30 + ci * 20}
              />
            ))}
          </div>
        ))}
      </div>

      {/* ── Footer spinner ─────────────────────────────────────── */}
      <div
        className="flex items-center justify-center gap-2 py-3"
        style={{ color: TEAL, fontSize: 12, flexShrink: 0 }}
      >
        <div
          className="animate-spin rounded-full"
          style={{
            width: 16, height: 16,
            border: "2px solid #c4dfe2",
            borderTopColor: TEAL,
          }}
        />
        <span style={{ fontWeight: 500 }}>Fetching data…</span>
      </div>
    </div>
  );
};
